/**
 * DocumentTemplateEditor - Create or edit global document templates
 *
 * Features:
 * - Create new templates via AI generation, PDF upload, or manual entry
 * - Dynamic categories from API
 * - Visibility prompt before saving new templates
 * - Auto-save with debounce for existing templates
 */
import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import {
  Button,
  Label,
  Input,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  cn,
} from '@respectly/ui';
import {
  ArrowLeft,
  Save,
  FileText,
  Loader2,
  Upload,
  Sparkles,
  Wand2,
} from 'lucide-react';
import { useAuth } from '@respectly/utils/auth';
import {
  EditorContextProvider,
  AIDocumentWizard,
  PDFUploadZone,
  type EditorConfig,
} from '@respectly/template-editor';
import type { NexusDocument } from '@respectly/shared-types';
import { useDocumentTemplatesStore } from '../../stores/useDocumentTemplatesStore';
import { useCategoriesStore, useActiveDocumentCategories } from '../../stores/useCategoriesStore';
import {
  VisibilityControlsPanel,
  VisibilityPromptModal,
  type VisibilityData,
} from '../../components/templates';

// Creation method type
type CreationMethod = 'ai' | 'upload' | 'manual' | null;

// Debounce utility
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

// Creation method card component
interface CreationMethodCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  recommended?: boolean;
  comingSoon?: boolean;
}

function CreationMethodCard({
  icon,
  title,
  description,
  onClick,
  recommended,
  comingSoon,
}: CreationMethodCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={comingSoon}
      className={cn(
        'relative flex flex-col items-center p-6 rounded-xl border-2 transition-all text-center',
        'hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/20',
        'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
        comingSoon && 'opacity-50 cursor-not-allowed hover:border-slate-200 hover:bg-transparent'
      )}
    >
      {recommended && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-blue-500 text-white text-xs font-medium rounded-full">
          Recommended
        </span>
      )}
      <div className="h-12 w-12 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="font-semibold text-slate-900 dark:text-white">{title}</h3>
      <p className="text-sm text-slate-500 mt-1">{description}</p>
      {comingSoon && (
        <span className="mt-2 text-xs text-slate-400">Coming Soon</span>
      )}
    </button>
  );
}

export function DocumentTemplateEditor() {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { id?: string };
  const isNewTemplate = !params.id || params.id === 'new';
  const { getAccessToken } = useAuth();

  // Stores
  const {
    activeTemplate,
    loadTemplate,
    createTemplate,
    updateTemplate,
    isLoadingTemplate,
    isPerformingAction,
    templateError,
    clearActiveTemplate,
  } = useDocumentTemplatesStore();

  const { loadDocumentCategories } = useCategoriesStore();
  const documentCategories = useActiveDocumentCategories();

  // Local form state
  const [creationMethod, setCreationMethod] = useState<CreationMethod>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<string>('');
  const [visibility, setVisibility] = useState<'all' | 'plans' | 'tenants' | 'none'>('all');
  const [visibleToPlans, setVisibleToPlans] = useState<string[]>([]);
  const [visibleToTenants, setVisibleToTenants] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [showVisibilityPrompt, setShowVisibilityPrompt] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Document content (NDF format)
  const [documentContent, setDocumentContent] = useState<NexusDocument | null>(null);

  // Editor context config for shared components
  const editorConfig: Partial<EditorConfig> = useMemo(() => ({
    getAccessToken,
    apiBaseUrl: import.meta.env.VITE_API_URL || 'http://localhost:3000',
    portalType: 'super-admin',
    features: {
      aiGeneration: true,
      pdfUpload: true,
      scanDocument: false,
      templateLibrary: false,
    },
    callbacks: {
      onNavigateBack: () => setCreationMethod(null),
      onError: (error: Error) => setSaveError(error.message),
    },
  }), [getAccessToken]);

  // Load categories on mount
  useEffect(() => {
    loadDocumentCategories();
  }, [loadDocumentCategories]);

  // Set default category when categories load
  useEffect(() => {
    if (documentCategories.length > 0 && !category) {
      setCategory(documentCategories[0]?.slug || '');
    }
  }, [documentCategories, category]);

  // Load template on mount if editing
  useEffect(() => {
    if (!isNewTemplate && params.id) {
      loadTemplate(params.id);
      setCreationMethod('manual'); // Editing uses manual mode
    }

    return () => {
      clearActiveTemplate();
    };
  }, [params.id, isNewTemplate, loadTemplate, clearActiveTemplate]);

  // Populate form when template loads
  useEffect(() => {
    if (activeTemplate) {
      setName(activeTemplate.name);
      setDescription(activeTemplate.description || '');
      setCategory(activeTemplate.category || '');
      setVisibility(activeTemplate.visibility as any);
      setVisibleToPlans(
        Array.isArray(activeTemplate.visibleToPlans)
          ? activeTemplate.visibleToPlans
          : activeTemplate.visibleToPlans
          ? JSON.parse(activeTemplate.visibleToPlans as any)
          : []
      );
      setVisibleToTenants(
        Array.isArray(activeTemplate.visibleToTenants)
          ? activeTemplate.visibleToTenants
          : activeTemplate.visibleToTenants
          ? JSON.parse(activeTemplate.visibleToTenants as any)
          : []
      );
      setIsActive(activeTemplate.isActive ?? true);

      // Parse content if exists
      if (activeTemplate.content) {
        try {
          const parsed =
            typeof activeTemplate.content === 'string'
              ? JSON.parse(activeTemplate.content)
              : activeTemplate.content;
          setDocumentContent(parsed);
        } catch (error) {
          console.error('Failed to parse template content:', error);
        }
      }
    }
  }, [activeTemplate]);

  // Auto-save (debounced) for existing templates
  const debouncedSave = useMemo(
    () =>
      debounce(async () => {
        if (!isNewTemplate && params.id) {
          await handleSaveInternal(false);
        }
      }, 2000),
    [isNewTemplate, params.id, name, description, category, visibility, visibleToPlans, visibleToTenants, documentContent]
  );

  const handleVisibilityChange = (data: VisibilityData) => {
    setVisibility(data.visibility);
    setVisibleToPlans(data.visibleToPlans);
    setVisibleToTenants(data.visibleToTenants);

    if (!isNewTemplate) {
      debouncedSave();
    }
  };

  // Internal save function
  const handleSaveInternal = async (showToast = true): Promise<boolean> => {
    const dto = {
      type: 'document' as const,
      name,
      description,
      category,
      visibility,
      visibleToPlans,
      visibleToTenants,
      isActive,
      content: documentContent ? JSON.stringify(documentContent) : JSON.stringify({ blocks: [] }),
    };

    if (isNewTemplate) {
      const newId = await createTemplate(dto);
      if (newId) {
        if (showToast) {
          console.log('Template created successfully');
        }
        navigate({ to: '/templates/documents' });
        return true;
      }
      return false;
    } else if (params.id) {
      const success = await updateTemplate(params.id, dto);
      if (success && showToast) {
        console.log('Template updated successfully');
      }
      return success;
    }
    return false;
  };

  // Handle save button click
  const handleSave = async () => {
    if (isNewTemplate) {
      // Show visibility prompt for new templates
      setShowVisibilityPrompt(true);
    } else {
      await handleSaveInternal(true);
    }
  };

  // Handle visibility prompt confirmation
  const handleVisibilityConfirm = async (data: VisibilityData) => {
    setVisibility(data.visibility);
    setVisibleToPlans(data.visibleToPlans);
    setVisibleToTenants(data.visibleToTenants);

    // Save with new visibility
    const dto = {
      type: 'document' as const,
      name,
      description,
      category,
      visibility: data.visibility,
      visibleToPlans: data.visibleToPlans,
      visibleToTenants: data.visibleToTenants,
      isActive,
      content: documentContent ? JSON.stringify(documentContent) : JSON.stringify({ blocks: [] }),
    };

    const newId = await createTemplate(dto);
    if (newId) {
      setShowVisibilityPrompt(false);
      navigate({ to: '/templates/documents' });
    } else {
      setSaveError('Failed to create template. Please try again.');
    }
  };

  const handleCancel = () => {
    navigate({ to: '/templates/documents' });
  };

  const handleSelectCreationMethod = (method: CreationMethod) => {
    setCreationMethod(method);
  };

  // Handle AI generation completion
  const handleAIComplete = (doc: NexusDocument) => {
    setDocumentContent(doc);
    setName(doc.meta.title || name || 'Untitled Document');
    setDescription(doc.meta.description || description);
    setCreationMethod('manual'); // Switch to manual mode to show form
  };

  // Handle PDF upload completion
  const handlePDFUploadSuccess = (document: any) => {
    // Create NDF from uploaded PDF
    const ndfDocument: NexusDocument = {
      version: '1.0',
      kind: 'document',
      meta: {
        title: document.name || 'Uploaded Document',
        description: '',
        variables: [],
      },
      blocks: [
        {
          id: crypto.randomUUID(),
          type: 'pdf_overlay',
          pdfUrl: document.url || '',
          pages: document.pages || [],
        } as any,
      ],
      settings: {
        submitButtonText: 'Submit',
        successMessage: 'Thank you for your submission!',
      },
    };
    setDocumentContent(ndfDocument);
    setName(document.name || name || 'Uploaded Document');
    setCreationMethod('manual'); // Switch to manual mode to show form
  };

  // Handle PDF upload error
  const handlePDFUploadError = (error: string) => {
    setSaveError(error);
  };

  // Loading state
  if (isLoadingTemplate) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <p className="text-sm text-slate-500">Loading template...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (templateError) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-500 font-semibold">Failed to load template</p>
          <p className="text-sm text-slate-500 mt-2">{templateError.message}</p>
          <Button onClick={handleCancel} variant="outline" className="mt-4">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // AI Generation view
  if (creationMethod === 'ai') {
    return (
      <EditorContextProvider config={editorConfig}>
        <div className="h-full">
          <AIDocumentWizard
            onComplete={handleAIComplete}
            onBack={() => setCreationMethod(null)}
            allowedKinds={['document', 'hybrid']}
          />
        </div>
      </EditorContextProvider>
    );
  }

  // PDF Upload view
  if (creationMethod === 'upload') {
    return (
      <EditorContextProvider config={editorConfig}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col h-full"
        >
          {/* Header */}
          <div className="shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
            <div className="flex items-center gap-3 p-4 lg:p-6">
              <Button variant="ghost" size="sm" onClick={() => setCreationMethod(null)} className="mr-2">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
                <Upload className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900 dark:text-white">
                  Upload PDF Document
                </h1>
                <p className="text-sm text-slate-500">
                  Upload a PDF and add signature fields and variables
                </p>
              </div>
            </div>
          </div>

          {/* Upload Zone */}
          <div className="flex-1 overflow-auto p-4 lg:p-6">
            <div className="max-w-3xl mx-auto">
              <PDFUploadZone
                onUploadSuccess={handlePDFUploadSuccess}
                onUploadError={handlePDFUploadError}
                onCancel={() => setCreationMethod(null)}
                getToken={getAccessToken}
                apiUrl={import.meta.env.VITE_API_URL || 'http://localhost:3000'}
              />
            </div>
          </div>
        </motion.div>
      </EditorContextProvider>
    );
  }

  // Creation method selection for new templates
  if (isNewTemplate && !creationMethod) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex flex-col h-full"
      >
        {/* Header */}
        <div className="shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <div className="flex items-center gap-3 p-4 lg:p-6">
            <Button variant="ghost" size="sm" onClick={handleCancel} className="mr-2">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-white">
                Create Document Template
              </h1>
              <p className="text-sm text-slate-500">
                Choose how you want to create your template
              </p>
            </div>
          </div>
        </div>

        {/* Creation Method Selection */}
        <div className="flex-1 overflow-auto p-4 lg:p-6">
          <div className="max-w-3xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <CreationMethodCard
                icon={<Sparkles className="h-6 w-6 text-purple-500" />}
                title="AI Generation"
                description="Describe your document and let AI create it"
                onClick={() => handleSelectCreationMethod('ai')}
                recommended
              />
              <CreationMethodCard
                icon={<Upload className="h-6 w-6 text-blue-500" />}
                title="Upload PDF"
                description="Upload a PDF and add signature fields"
                onClick={() => handleSelectCreationMethod('upload')}
              />
              <CreationMethodCard
                icon={<Wand2 className="h-6 w-6 text-amber-500" />}
                title="Manual Entry"
                description="Create template from scratch"
                onClick={() => handleSelectCreationMethod('manual')}
              />
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Editor view
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col h-full"
    >
      {/* Header */}
      <div className="shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
        <div className="flex items-center justify-between p-4 lg:p-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={isNewTemplate ? () => setCreationMethod(null) : handleCancel}
              className="mr-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-white">
                {isNewTemplate ? 'Create Document Template' : 'Edit Document Template'}
              </h1>
              <p className="text-sm text-slate-500">
                {isNewTemplate
                  ? 'Create a new global document template for your tenants'
                  : 'Edit the template details and visibility settings'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isPerformingAction || !name.trim()}
            >
              {isPerformingAction ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isNewTemplate ? 'Create Template' : 'Save Changes'}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-5xl mx-auto p-4 lg:p-6 space-y-6">
          {/* Basic Information */}
          <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              Basic Information
            </h2>

            <div className="space-y-4">
              <div>
                <Label htmlFor="name">
                  Template Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (!isNewTemplate) debouncedSave();
                  }}
                  placeholder="e.g., Sales Contract"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    if (!isNewTemplate) debouncedSave();
                  }}
                  placeholder="Describe what this document template is for..."
                  className="mt-2"
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="category">Category</Label>
                <Select
                  value={category}
                  onValueChange={(value) => {
                    setCategory(value);
                    if (!isNewTemplate) debouncedSave();
                  }}
                >
                  <SelectTrigger id="category" className="mt-2">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {documentCategories.map((cat) => (
                      <SelectItem key={cat.slug} value={cat.slug}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Visibility Controls - Only show for existing templates */}
          {!isNewTemplate && (
            <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
              <VisibilityControlsPanel
                visibility={visibility}
                visibleToPlans={visibleToPlans}
                visibleToTenants={visibleToTenants}
                onChange={handleVisibilityChange}
              />
            </div>
          )}

          {/* Document Content Preview */}
          <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
              Document Content
            </h2>
            {documentContent ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                    {documentContent.kind}
                  </span>
                  <span>{documentContent.blocks.length} blocks</span>
                  {documentContent.meta.variables && documentContent.meta.variables.length > 0 && (
                    <span>{documentContent.meta.variables.length} variables</span>
                  )}
                </div>
                <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-slate-50 dark:bg-slate-800">
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Document generated successfully. Full editor coming soon.
                  </p>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-lg p-12 text-center">
                <FileText className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 dark:text-slate-400 font-medium">
                  No content yet
                </p>
                <p className="text-sm text-slate-500 mt-2">
                  Use AI Generation or PDF Upload to add content to this template.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Visibility Prompt Modal */}
      <VisibilityPromptModal
        open={showVisibilityPrompt}
        onOpenChange={setShowVisibilityPrompt}
        templateName={name || 'Untitled Template'}
        templateType="document"
        onConfirm={handleVisibilityConfirm}
        onCancel={() => setShowVisibilityPrompt(false)}
        isSaving={isPerformingAction}
        error={saveError}
      />
    </motion.div>
  );
}
